import { getEntitiesByFpIdReviewsOptions, getOrgFrequentNotesOptions, postEntitiesByFpIdActionsMutation, postOrgFrequentNotesMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { trackAction } from '@onefootprint/logger';
import type { LabelKind, TerminalDecisionStatus } from '@onefootprint/request-types/dashboard';
import { Dialog, useToast } from '@onefootprint/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useEntityId from '@/entity/hooks/use-entity-id';
import useEditLabel from '@/hooks/use-edit-label';
import useInvalidateQueries from '@/hooks/use-invalidate-queries';
import ManualReviewForm, { type ManualReviewFormData } from './manual-review-dialog-form';
export type ManualReviewDialogProps = {
  onClose: () => void;
  open: boolean;
  status: TerminalDecisionStatus;
};
const ManualReviewDialog = ({
  open,
  onClose,
  status
}: ManualReviewDialogProps) => {
  const {
    t
  } = useTranslation('entity-details', {
    keyPrefix: 'manualReview'
  });
  const fpId = useEntityId();
  const {
    toast
  } = useToast();
  const showRequestErrorToast = useRequestErrorToast();
  const invalidateQueries = useInvalidateQueries();
  const formRef = useRef<HTMLFormElement>(null);
  const createMutation = useMutation(postOrgFrequentNotesMutation());
  const editLabel = useEditLabel();
  const actionMutation = useMutation(postEntitiesByFpIdActionsMutation());
  const {
    data: pendingReviews
  } = useQuery({
    ...getEntitiesByFpIdReviewsOptions({
      path: {
        fpId
      }
    }),
    enabled: !!fpId && open
  });
  const formMethods = useForm<ManualReviewFormData>({
    defaultValues: {
      note: '',
      isPinned: false,
      frequentNote: false,
      reviewsToClear: []
    }
  });

  // Pre-select all pending reviews when they load
  useEffect(() => {
    if (pendingReviews && pendingReviews.length > 0) {
      const currentSelection = formMethods.getValues('reviewsToClear');
      if (currentSelection.length === 0) {
        formMethods.setValue('reviewsToClear', pendingReviews.map(r => r.id));
      }
    }
  }, [pendingReviews, formMethods]);
  const [note] = useWatch({
    control: formMethods.control,
    name: ['note']
  });
  const frequentNoteKind = status === 'pass' ? 'manual_review_pass' : 'manual_review_fail';
  const {
    data: frequentNotes
  } = useQuery(getOrgFrequentNotesOptions({
    query: {
      kind: frequentNoteKind
    }
  }));
  const hasNote = frequentNotes?.some(({
    content
  }) => content === note);
  const createFrequentNote = (note_0: string) => {
    try {
      createMutation.mutateAsync({
        body: {
          content: note_0,
          kind: frequentNoteKind
        }
      });
    } catch (error) {
      showRequestErrorToast(error);
    }
  };
  const updateFraudLabel = (label: LabelKind) => {
    try {
      editLabel.mutateAsync({
        body: {
          kind: label
        },
        path: {
          fpId
        }
      });
    } catch (error_0) {
      showRequestErrorToast(error_0);
    }
  };
  const hasPendingReviews = (pendingReviews ?? []).length > 0;
  const manualReviewAction = async (isPinned: boolean, note_1: string, reviewsToClear: string[]) => {
    try {
      await actionMutation.mutateAsync({
        path: {
          fpId
        },
        body: {
          actions: [{
            kind: 'manual_decision',
            status,
            annotation: {
              isPinned,
              note: note_1
            },
            // Absent means "clear all" (backwards compat); an empty array means "clear none".
            ...(hasPendingReviews ? {
              reviewsToClear
            } : {})
          }]
        }
      });
    } catch (error_1) {
      showRequestErrorToast(error_1);
    }
  };
  const handleSubmit = async ({
    frequentNote,
    note: note_2,
    isPinned: isPinned_0,
    label: label_0,
    reviewsToClear: reviewsToClear_0
  }: ManualReviewFormData) => {
    if (frequentNote && !hasNote) {
      createFrequentNote(note_2);
    }
    trackAction('manual_review_performed', {
      entity_id: fpId,
      decision_status: status
    });
    try {
      await manualReviewAction(isPinned_0, note_2, reviewsToClear_0);
      if (label_0) {
        updateFraudLabel(label_0);
      }
      formMethods.reset();
      toast({
        title: t($ => $.feedback.success.title),
        description: t($_0 => $_0.feedback.success.description)
      });
      onClose();
      invalidateQueries();
    } catch (error_2) {
      showRequestErrorToast(error_2);
    }
  };
  return <Dialog size="compact" title={t($_1 => $_1.dialog.title)} onClose={onClose} open={open} primaryButton={{
    form: 'manual-review-form',
    label: t($_2 => $_2.dialog.complete),
    loading: actionMutation.isPending,
    type: 'submit'
  }} secondaryButton={{
    disabled: actionMutation.isPending,
    label: t($_3 => $_3.dialog.cancel),
    onClick: onClose
  }}>
      <FormProvider {...formMethods}>
        <form id="manual-review-form" ref={formRef} onSubmit={formMethods.handleSubmit(handleSubmit)}>
          <ManualReviewForm frequentNotes={frequentNotes ?? []} frequentNoteKind={frequentNoteKind} pendingReviews={pendingReviews ?? []} status={status} />
        </form>
      </FormProvider>
    </Dialog>;
};
export default ManualReviewDialog;